import { useEffect } from "react";
import { SITE } from "../lib/site";

/**
 * Lightweight SEO head manager.
 * Sets title, description, keywords, canonical, OG, Twitter Card, JSON-LD (page + breadcrumbs).
 */
export default function SeoHead({
  title,
  description,
  canonical,
  image,
  schema,
  breadcrumbs,
  keywords,
  noindex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
    };

    if (description) {
      ensureMeta('meta[name="description"]', {
        create: { name: "description" },
        set: { content: description },
      });
      ensureMeta('meta[property="og:description"]', {
        create: { property: "og:description" },
        set: { content: description },
      });
      ensureMeta('meta[name="twitter:description"]', {
        create: { name: "twitter:description" },
        set: { content: description },
      });
    }
    if (keywords && keywords.length) {
      ensureMeta('meta[name="keywords"]', {
        create: { name: "keywords" },
        set: { content: keywords.join(", ") },
      });
    }
    if (title) {
      ensureMeta('meta[property="og:title"]', {
        create: { property: "og:title" },
        set: { content: title },
      });
      ensureMeta('meta[name="twitter:title"]', {
        create: { name: "twitter:title" },
        set: { content: title },
      });
    }
    if (image) {
      ensureMeta('meta[property="og:image"]', {
        create: { property: "og:image" },
        set: { content: image },
      });
      ensureMeta('meta[name="twitter:image"]', {
        create: { name: "twitter:image" },
        set: { content: image },
      });
    }
    ensureMeta('meta[property="og:type"]', {
      create: { property: "og:type" },
      set: { content: "website" },
    });
    ensureMeta('meta[property="og:locale"]', {
      create: { property: "og:locale" },
      set: { content: "fr_FR" },
    });
    ensureMeta('meta[property="og:site_name"]', {
      create: { property: "og:site_name" },
      set: { content: SITE.name },
    });
    ensureMeta('meta[name="twitter:card"]', {
      create: { name: "twitter:card" },
      set: { content: "summary_large_image" },
    });
    ensureMeta('meta[name="robots"]', {
      create: { name: "robots" },
      set: { content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large" },
    });
    ensureMeta('meta[name="geo.region"]', {
      create: { name: "geo.region" },
      set: { content: "FR-33" },
    });
    ensureMeta('meta[name="geo.placename"]', {
      create: { name: "geo.placename" },
      set: { content: "Bordeaux, Gironde" },
    });
    ensureMeta('meta[name="geo.position"]', {
      create: { name: "geo.position" },
      set: { content: `${SITE.geo.lat};${SITE.geo.lng}` },
    });

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      ensureMeta('meta[property="og:url"]', {
        create: { property: "og:url" },
        set: { content: canonical },
      });
    }

    // JSON-LD: page schema (supports array of schemas)
    document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((sc, idx) => {
        const s = document.createElement("script");
        s.setAttribute("type", "application/ld+json");
        s.setAttribute("data-seo-jsonld", `page-${idx}`);
        s.textContent = JSON.stringify(sc);
        document.head.appendChild(s);
      });
    }
    // JSON-LD: breadcrumbs
    if (breadcrumbs && breadcrumbs.length) {
      const b = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          item: c.url,
        })),
      };
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-seo-jsonld", "breadcrumbs");
      s.textContent = JSON.stringify(b);
      document.head.appendChild(s);
    }
  }, [title, description, canonical, image, JSON.stringify(schema || {}), JSON.stringify(breadcrumbs || []), JSON.stringify(keywords || []), noindex]);

  return null;
}
